import { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { HiCheck, HiClipboardCopy, HiPlus, HiRefresh } from "react-icons/hi";
import { CgSpinner } from "react-icons/cg";

import useShareLinks from "services/fileApi/hooks/useShareLinks";
import useMintShareLink from "services/fileApi/hooks/useMintShareLink";
import useRevokeShareLink from "services/fileApi/hooks/useRevokeShareLink";
import styles from "./ShareLinkModal.module.scss";

interface ShareLinkModalProps {
  fileName: string;
  fileId: string;
  onClose: () => void;
}

/** How long the "Copied!" / "Extended" confirmations stay visible. */
const CONFIRMATION_MS = 2000;

function formatExpiry(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

/**
 * Share a file via a durable link (file-api ADR 036). Reads the active share
 * link (url, status, expiry) when opened and shows its url in a read-only field
 * with an inline copy action, or a "Create share link" action when the file
 * isn't shared, plus an "Extend" action (mint is idempotent get-or-renew).
 * "Stop sharing" revokes the active link.
 *
 * The parent (ShareFileButton) mounts this only while open, so all component
 * state resets on close and the status query re-runs on each open — no manual
 * reset needed. Data access goes through React Query hooks (useShareLinks /
 * useMintShareLink / useRevokeShareLink). Opening only reads (GET) and the list
 * endpoint carries the redeemable url; minting — which renews the link's
 * expiry — is reserved for the deliberate Create/Extend actions.
 */
export default function ShareLinkModal({
  fileName,
  fileId,
  onClose,
}: ShareLinkModalProps) {
  const shareLinks = useShareLinks({ fileId });
  const mint = useMintShareLink(fileId);
  const revoke = useRevokeShareLink(fileId);

  // Which mint-backed action is in flight (create and extend share one
  // mutation) and the transient success confirmations. UI-only — not server
  // state.
  const [mintAction, setMintAction] = useState<"create" | "extend">("create");
  const [isCopied, setIsCopied] = useState(false);
  const [isExtended, setIsExtended] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Both confirmations revert themselves after a couple of seconds. Scheduling
  // the reset here rather than in the click handler means the timer is cleared
  // when the modal closes, so it can't set state on an unmounted modal.
  useEffect(() => {
    if (!isCopied) return;
    const timer = setTimeout(() => setIsCopied(false), CONFIRMATION_MS);
    return () => clearTimeout(timer);
  }, [isCopied]);

  useEffect(() => {
    if (!isExtended) return;
    const timer = setTimeout(() => setIsExtended(false), CONFIRMATION_MS);
    return () => clearTimeout(timer);
  }, [isExtended]);

  const activeLink = shareLinks.data?.find((item) => item.status === "active");
  const minted = mint.data ?? null;
  const hasLink = Boolean(activeLink || minted);
  const token = activeLink?.token ?? minted?.token;
  const expiresAt = minted?.expires_at ?? activeLink?.expires_at;
  // The list endpoint returns the redeemable url, so an active link's url is
  // available on open without minting; fall back to a just-minted result.
  const url = activeLink?.url ?? minted?.url ?? null;

  const handleCreate = () => {
    if (mint.isPending) return;
    setMintAction("create");
    mint.mutate();
  };

  const handleExtend = () => {
    if (mint.isPending) return;
    setMintAction("extend");
    mint.mutate(undefined, {
      onSuccess: () => setIsExtended(true),
    });
  };

  const handleCopyLink = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setIsCopied(true);
    } catch (error) {
      console.error("Failed to copy share link:", error);
    }
  };

  const handleStopSharing = () => {
    if (revoke.isPending || !token) return;
    revoke.mutate(token, {
      onSuccess: () => {
        // Clear the minted result too, otherwise the view stays in the
        // "shared" state after the active link is dropped from the cache.
        mint.reset();
        setIsCopied(false);
        setIsExtended(false);
      },
    });
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  const renderStatus = () => {
    if (shareLinks.isLoading) {
      return <span className={styles.status}>Checking status…</span>;
    }

    if (shareLinks.isError) {
      return (
        <span className={`${styles.status} ${styles.statusError}`}>
          <span className={styles.statusDot}>●</span>
          <span>Couldn&apos;t check sharing status</span>
          <button
            className={styles.extend}
            onClick={() => shareLinks.refetch()}
            title="Retry"
          >
            <HiRefresh size={12} />
            Retry
          </button>
        </span>
      );
    }

    return (
      <span
        className={`${styles.status} ${hasLink ? styles.statusActive : ""}`}
      >
        <span className={styles.statusDot}>{hasLink ? "●" : "○"}</span>
        <span>
          {hasLink && expiresAt
            ? `Link active · expires ${formatExpiry(expiresAt)}`
            : "Not shared yet"}
        </span>
        {hasLink && (
          <button
            className={styles.extend}
            onClick={handleExtend}
            disabled={mint.isPending}
            title="Extend expiry"
          >
            {mint.isPending && mintAction === "extend" ? (
              <CgSpinner
                size={12}
                style={{ animation: "spin 1s linear infinite" }}
              />
            ) : isExtended ? (
              <HiCheck size={12} />
            ) : (
              <HiRefresh size={12} />
            )}
            {isExtended ? "Extended" : "Extend"}
          </button>
        )}
      </span>
    );
  };

  const renderBody = () => {
    if (shareLinks.isLoading || shareLinks.isError) return null;

    if (!hasLink) {
      return (
        <button
          className={styles.primary}
          onClick={handleCreate}
          disabled={mint.isPending}
        >
          {mint.isPending && mintAction === "create" ? (
            <CgSpinner
              size={14}
              style={{ animation: "spin 1s linear infinite" }}
            />
          ) : (
            <>
              <HiPlus size={14} className="mr1" />
              Create share link
            </>
          )}
        </button>
      );
    }

    return (
      <>
        <div className={styles.urlRow}>
          {url && (
            <input
              className={styles.field}
              type="text"
              readOnly
              value={url}
              onFocus={(event) => event.currentTarget.select()}
            />
          )}
          <button
            className={`${styles.copy} ${isCopied ? styles.success : ""}`}
            onClick={handleCopyLink}
          >
            {isCopied ? (
              <>
                <HiCheck size={14} className="mr1" />
                Copied!
              </>
            ) : (
              <>
                <HiClipboardCopy size={14} className="mr1" />
                Copy link
              </>
            )}
          </button>
        </div>
        <span className={styles.notice}>
          Any Vylor user with the link can download this file.
        </span>
        <button
          className={styles.stop}
          onClick={handleStopSharing}
          disabled={revoke.isPending}
        >
          {revoke.isPending ? (
            <CgSpinner
              size={14}
              style={{ animation: "spin 1s linear infinite" }}
            />
          ) : (
            "Stop sharing"
          )}
        </button>
      </>
    );
  };

  const modalContent = (
    <div className={styles.backdrop} onClick={handleBackdropClick}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <span className={styles.fileName} title={fileName}>
            Share &quot;{fileName}&quot;
          </span>
          <button
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Close share dialog"
          >
            ✕
          </button>
        </div>
        <div className={styles.body}>
          {renderStatus()}
          {renderBody()}
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
}
